/**
 * @fileoverview Renders "lesson markdown" (see `lib/richText.js`) as React
 * nodes — the read-only counterpart of the FormatToolbar.
 *
 * Built from parsed nodes rather than `dangerouslySetInnerHTML`: the text is
 * instructor-authored and travels to families, so the one rule worth enforcing
 * structurally is that no stored string can ever become markup here.
 */

import { parseRichText } from "@/lib/richText";

const emphasis = (run, key) => {
  if (run.type === "bolditalic") {
    return <strong key={key} className="font-semibold"><em>{run.value}</em></strong>;
  }
  if (run.type === "bold") return <strong key={key} className="font-semibold">{run.value}</strong>;
  if (run.type === "italic") return <em key={key}>{run.value}</em>;
  return <span key={key}>{run.value}</span>;
};

const renderRuns = (runs, keyPrefix) =>
  runs.map((run, i) => {
    const key = `${keyPrefix}-${i}`;
    return run.underline ? (
      <u key={key} className="underline-offset-2">
        {emphasis(run)}
      </u>
    ) : (
      emphasis(run, key)
    );
  });

/**
 * @param {Object} props
 * @param {string} props.value - Raw stored lesson text.
 * @param {string} [props.className] - Classes for the wrapper (typography lives here).
 * @param {string} [props.markerClassName] - Classes for bullet/number markers.
 * @param {string} [props.testId]
 */
export default function RichText({ value, className = "", markerClassName = "text-[var(--text-tertiary)]", testId }) {
  const blocks = parseRichText(value);
  if (blocks.length === 0) return null;

  return (
    <div className={`flex flex-col gap-3 ${className}`} data-testid={testId}>
      {blocks.map((block, bi) => {
        if (block.type === "paragraph") {
          return (
            <p key={bi} className="m-0">
              {block.lines.map((runs, li) => (
                // A single newline inside a paragraph is a line break the
                // instructor typed on purpose — it has to survive to the page.
                <span key={li}>
                  {li > 0 && <br />}
                  {renderRuns(runs, `${bi}-${li}`)}
                </span>
              ))}
            </p>
          );
        }
        const ordered = block.type === "ordered";
        return (
          <ul key={bi} className="m-0 flex list-none flex-col gap-1.5 p-0">
            {block.lines.map((runs, li) => (
              <li key={li} className="flex items-start gap-2.5">
                <span className={`shrink-0 tabular-nums ${markerClassName}`}>
                  {ordered ? `${(block.start ?? 1) + li}.` : "•"}
                </span>
                <span className="min-w-0 flex-1">{renderRuns(runs, `${bi}-${li}`)}</span>
              </li>
            ))}
          </ul>
        );
      })}
    </div>
  );
}
